import { useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  Heart,
  Newspaper,
  Lightning,
  User,
  Storefront,
  ForkKnife,
  Buildings,
  TreePalm,
  Butterfly,
  PaintBrush,
  FirstAid,
  Detective,
  Airplane,
  Images,
  PencilSimple,
  Briefcase,
  Waves,
  MusicNote,
  Aperture,
  Baby,
  Rocket,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const careers = {
  en: {
    page_title: "Photo Careers",
    page_sub: "Explore the many professional paths in photography — from weddings to forensics, there's a niche for every passion.",
    typical_work: "Typical Work",
    skills_needed: "Key Skills",
    salary_range: "Salary Range (USD)",
    items: [
      {
        id: "wedding",
        icon: "Heart",
        title: "Wedding Photographer",
        desc: "Capture one of the most important days in people's lives, from candid emotions to formal group portraits.",
        work: "Engagement shoots, ceremony coverage, reception, editing 500-2000+ images per event, album design. Often weekend work with peak season in spring/summer.",
        skills: "People skills & posing direction, fast-paced adaptability, flash and ambient light mixing, storytelling through sequences, business and marketing.",
        salary: "$30,000 – $75,000+ (highly variable; top photographers earn $100K+)",
      },
      {
        id: "fashion",
        icon: "Aperture",
        title: "Fashion Photographer",
        desc: "Create editorial and commercial images for magazines, designers, and brands showcasing clothing and style.",
        work: "Studio and on-location shoots, collaboration with stylists/makeup artists/art directors, editorial spreads, lookbooks, e-commerce catalogs.",
        skills: "Lighting mastery (studio strobes), creative direction, trend awareness, retouching, team management, networking in the fashion industry.",
        salary: "$35,000 – $90,000+ (editorial pays less than commercial; top fashion photographers earn significantly more)",
      },
      {
        id: "photojournalism",
        icon: "Newspaper",
        title: "Photojournalist",
        desc: "Tell visual stories that document news events, social issues, and human experiences for media outlets.",
        work: "Breaking news coverage, feature stories, documentary projects, wire services, newspapers, magazines, and online publications.",
        skills: "Fast instincts, ethical judgment, storytelling, working under pressure, caption writing, understanding of current events and cultures.",
        salary: "$25,000 – $65,000 (staff); freelance varies widely. Pulitzer-level work can lead to grants and book deals.",
      },
      {
        id: "sports",
        icon: "Lightning",
        title: "Sports Photographer",
        desc: "Freeze the decisive moments of athletic competition with fast reflexes and long telephoto lenses.",
        work: "Sideline coverage at games, tournaments, and events. Editorial for newspapers, magazines, team websites, and sports brands.",
        skills: "Anticipation of peak action, fast continuous shooting, long lens handling, understanding of sports rules, quick turnaround editing.",
        salary: "$30,000 – $70,000 (staff); elite sports photographers with agency contracts earn $100K+",
      },
      {
        id: "portrait",
        icon: "User",
        title: "Portrait / Studio Photographer",
        desc: "Create professional headshots, family portraits, senior photos, and personal branding imagery in a studio or on-location.",
        work: "Client sessions (individuals, families, corporate), studio setup and lighting, editing, print sales and digital delivery.",
        skills: "Posing and directing subjects, studio lighting, client communication, consistent editing style, sales and marketing.",
        salary: "$28,000 – $60,000 (studio employee); self-employed portrait photographers can earn $50K-$100K+",
      },
      {
        id: "commercial",
        icon: "Storefront",
        title: "Commercial / Advertising Photographer",
        desc: "Produce images for advertising campaigns, billboards, websites, and marketing materials for brands.",
        work: "Pre-production planning with agencies, studio and location shoots, product and lifestyle images, team collaboration.",
        skills: "High-end lighting and composition, creative concept development, client and agency communication, post-production expertise, licensing knowledge.",
        salary: "$40,000 – $100,000+ (one of the highest-paying photography fields)",
      },
      {
        id: "food",
        icon: "ForkKnife",
        title: "Food Photographer",
        desc: "Make food look irresistible for restaurants, cookbooks, packaging, and social media content.",
        work: "Restaurant menu shoots, cookbook imagery, recipe blog content, packaging design photos, food brand campaigns.",
        skills: "Styling collaboration (or self-styling), control of natural and artificial light, color accuracy, close-up/macro skills, understanding of food preparation timelines.",
        salary: "$30,000 – $70,000 (staff/freelance); top food photographers with brand clients earn $80K+",
      },
      {
        id: "architecture",
        icon: "Buildings",
        title: "Architecture / Real Estate Photographer",
        desc: "Photograph buildings, interiors, and properties for architects, developers, and real estate agents.",
        work: "Interior and exterior shoots, virtual tours, aerial/drone photography, twilight shoots, HDR and perspective correction.",
        skills: "Tilt-shift and wide-angle lens expertise, HDR bracketing, perspective correction, drone piloting (FAA Part 107), ambient and flash blending.",
        salary: "$35,000 – $80,000 (real estate volume shooters can earn $100K+ with high volume)",
      },
      {
        id: "wildlife",
        icon: "Butterfly",
        title: "Wildlife / Nature Photographer",
        desc: "Document animals and natural environments for conservation, editorial, and fine art purposes.",
        work: "Fieldwork in remote locations, long hours waiting for animal behavior, editorial submissions, conservation partnerships, print sales and exhibitions.",
        skills: "Extreme patience, knowledge of animal behavior, long telephoto technique, survival/outdoor skills, post-processing for natural look.",
        salary: "$25,000 – $60,000 (many supplement with workshops, prints, and stock licensing; top wildlife photographers earn more through books and grants)",
      },
      {
        id: "fineart",
        icon: "PaintBrush",
        title: "Fine Art Photographer",
        desc: "Create photographs as artistic expression for galleries, museums, collectors, and exhibitions.",
        work: "Personal projects, gallery exhibitions, art fairs, limited edition print sales, artist residencies, teaching.",
        skills: "Strong artistic vision, conceptual thinking, printing and presentation expertise, gallery networking, grant writing.",
        salary: "Highly variable ($15,000 – $100,000+); income from print sales, exhibitions, grants, teaching, and commissions.",
      },
      {
        id: "medical",
        icon: "FirstAid",
        title: "Medical / Scientific Photographer",
        desc: "Create precise images for medical records, research publications, textbooks, and surgical documentation.",
        work: "Operating room documentation, pathology imaging, research lab photography, medical device catalogs, patient condition documentation.",
        skills: "Technical precision, sterile environment protocols, macro photography, color accuracy, understanding of medical terminology and anatomy.",
        salary: "$40,000 – $70,000 (hospital staff); specialized roles with advanced certifications can earn more.",
      },
      {
        id: "forensic",
        icon: "Detective",
        title: "Forensic Photographer",
        desc: "Document crime scenes, evidence, and accidents with precise, unaltered images for legal proceedings.",
        work: "Crime scene documentation, evidence photography, accident reconstruction imagery, courtroom presentation, police department or private forensic lab.",
        skills: "Meticulous attention to detail, chain-of-custody awareness, macro and scale photography, flash and ambient balance, report writing.",
        salary: "$35,000 – $65,000 (law enforcement); private forensic consultants may earn more.",
      },
      {
        id: "aerial",
        icon: "Airplane",
        title: "Aerial / Drone Photographer",
        desc: "Capture stunning bird's-eye perspectives for real estate, film, mapping, and creative projects.",
        work: "Real estate aerial tours, construction progress documentation, film/video production, mapping and surveying, creative projects.",
        skills: "Drone piloting (FAA Part 107 certification required in US), understanding of airspace regulations, composition from above, video skills, weather assessment.",
        salary: "$35,000 – $80,000 (real estate drone pilots); specialized industrial or film work can pay significantly more.",
      },
      {
        id: "stock",
        icon: "Images",
        title: "Stock Photographer",
        desc: "Build a library of images licensed through stock agencies for use in ads, websites, and publications worldwide.",
        work: "Self-directed shoots based on market trends, keyword optimization, managing portfolios on multiple stock platforms (Getty, Shutterstock, Adobe Stock).",
        skills: "Market research and trend awareness, versatile shooting style, model releases and legal knowledge, high-volume editing workflow, SEO and keywording.",
        salary: "Highly variable ($5,000 – $100,000+); top contributors with large portfolios earn passive income.",
      },
      {
        id: "editor",
        icon: "PencilSimple",
        title: "Photo Editor / Retoucher",
        desc: "Specialize in post-production, transforming raw images into polished final products for studios, agencies, and publications.",
        work: "Color correction, skin retouching, composite creation, batch editing, magazine layout collaboration, e-commerce image processing.",
        skills: "Advanced Photoshop and Lightroom, color science, attention to detail, fast turnaround, understanding of client style preferences.",
        salary: "$30,000 – $65,000 (in-house); freelance retouchers specializing in beauty/fashion can earn $80K+.",
      },
      {
        id: "travel",
        icon: "TreePalm",
        title: "Travel Photographer",
        desc: "Document destinations, cultures, and experiences for tourism boards, magazines, and brands.",
        work: "Destination shoots, cultural documentation, hotel and resort photography, travel blog content, magazine features, social media campaigns.",
        skills: "Adaptability to diverse environments, storytelling, light-packing efficiency, cultural sensitivity, writing/blogging skills, social media presence.",
        salary: "$25,000 – $70,000 (staff/freelance); brand partnerships and social media influence can increase income significantly.",
      },
      {
        id: "underwater",
        icon: "Waves",
        title: "Underwater Photographer",
        desc: "Capture marine life, underwater landscapes, and human subjects beneath the water's surface.",
        work: "Marine biology documentation, resort and dive tourism imagery, editorial/conservation work, fashion underwater shoots.",
        skills: "Scuba certification, underwater housing expertise, strobe placement underwater, color correction (water absorbs red), marine biology knowledge.",
        salary: "$30,000 – $70,000; specialized resort and conservation work can pay more. Equipment costs are high.",
      },
      {
        id: "concert",
        icon: "MusicNote",
        title: "Concert / Music Photographer",
        desc: "Photograph live performances, album art, and promotional images for musicians and venues.",
        work: "Live show coverage (often first 3 songs, no flash), album artwork, band promo shoots, festival coverage, music magazine editorials.",
        skills: "Low-light mastery, fast prime lenses, timing and anticipation, photo pit etiquette, networking with publicists and artists, quick turnaround.",
        salary: "$20,000 – $55,000 (freelance/editorial); top concert photographers with touring contracts earn more.",
      },
      {
        id: "newborn",
        icon: "Baby",
        title: "Newborn / Family Photographer",
        desc: "Specialize in the delicate art of photographing newborns, babies, and families during milestone moments.",
        work: "Newborn sessions (first 2 weeks), maternity shoots, milestone sessions (3, 6, 12 months), family portraits, cake smash sessions.",
        skills: "Newborn safety and posing (critical), patience, soothing techniques, soft/warm lighting, gentle editing style, parent communication.",
        salary: "$25,000 – $60,000 (studio employee); self-employed newborn specialists in metro areas can earn $80K+.",
      },
      {
        id: "space",
        icon: "Rocket",
        title: "Astrophotographer",
        desc: "Capture the night sky, celestial events, and deep-space objects using specialized long-exposure techniques.",
        work: "Nightscape photography, Milky Way compositions, star trails, lunar/planetary imaging, deep-sky telescope photography, timelapses.",
        skills: "Long-exposure mastery, understanding of celestial mechanics, polar alignment, stacking software (DeepSkyStacker, Sequator), dark-site logistics, post-processing.",
        salary: "$20,000 – $50,000 (mostly freelance/print sales); income supplemented by workshops, tutorials, and stock licensing.",
      },
    ],
  },
  es: {
    page_title: "Carreras Fotograficas",
    page_sub: "Explora los muchos caminos profesionales en fotografia — desde bodas hasta fotografia forense, hay un nicho para cada pasion.",
    typical_work: "Trabajo Tipico",
    skills_needed: "Habilidades Clave",
    salary_range: "Rango Salarial (USD)",
    items: [
      {
        id: "wedding",
        icon: "Heart",
        title: "Fotografo de Bodas",
        desc: "Capturar uno de los dias mas importantes en la vida de las personas, desde emociones espontaneas hasta retratos grupales formales.",
        work: "Sesiones de compromiso, cobertura de ceremonia, recepcion, edicion de 500-2000+ imagenes por evento, diseno de album. Generalmente trabajo de fin de semana con temporada alta en primavera/verano.",
        skills: "Habilidades interpersonales y direccion de poses, adaptabilidad rapida, mezcla de flash y luz ambiente, narrativa visual, negocios y marketing.",
        salary: "$30,000 – $75,000+ (altamente variable; los mejores fotografos ganan $100K+)",
      },
      {
        id: "fashion",
        icon: "Aperture",
        title: "Fotografo de Moda",
        desc: "Crear imagenes editoriales y comerciales para revistas, disenadores y marcas mostrando ropa y estilo.",
        work: "Sesiones en estudio y locacion, colaboracion con estilistas/maquilladores/directores de arte, spreads editoriales, lookbooks, catalogos de e-commerce.",
        skills: "Dominio de iluminacion (strobes de estudio), direccion creativa, conocimiento de tendencias, retoque, gestion de equipo, networking en la industria de la moda.",
        salary: "$35,000 – $90,000+ (editorial paga menos que comercial; los mejores ganan significativamente mas)",
      },
      {
        id: "photojournalism",
        icon: "Newspaper",
        title: "Fotoperiodista",
        desc: "Contar historias visuales que documentan eventos noticiosos, problemas sociales y experiencias humanas para medios de comunicacion.",
        work: "Cobertura de noticias de ultimo momento, reportajes, proyectos documentales, agencias de noticias, periodicos, revistas y publicaciones en linea.",
        skills: "Instintos rapidos, juicio etico, narrativa, trabajo bajo presion, redaccion de pies de foto, comprension de eventos actuales y culturas.",
        salary: "$25,000 – $65,000 (plantilla); freelance varia ampliamente.",
      },
      {
        id: "sports",
        icon: "Lightning",
        title: "Fotografo Deportivo",
        desc: "Congelar los momentos decisivos de la competencia atletica con reflejos rapidos y teleobjetivos largos.",
        work: "Cobertura al borde del campo en juegos, torneos y eventos. Editorial para periodicos, revistas, sitios web de equipos y marcas deportivas.",
        skills: "Anticipacion de la accion cumbre, disparo continuo rapido, manejo de lentes largos, comprension de reglas deportivas, edicion rapida.",
        salary: "$30,000 – $70,000 (plantilla); fotografos deportivos de elite con contratos de agencia ganan $100K+",
      },
      {
        id: "portrait",
        icon: "User",
        title: "Fotografo de Retrato / Estudio",
        desc: "Crear retratos profesionales, fotos familiares, fotos de graduacion e imagenes de marca personal en estudio o locacion.",
        work: "Sesiones con clientes (individuos, familias, corporativos), montaje de estudio e iluminacion, edicion, venta de impresiones y entrega digital.",
        skills: "Direccion de poses, iluminacion de estudio, comunicacion con clientes, estilo de edicion consistente, ventas y marketing.",
        salary: "$28,000 – $60,000 (empleado de estudio); fotografos independientes pueden ganar $50K-$100K+",
      },
      {
        id: "commercial",
        icon: "Storefront",
        title: "Fotografo Comercial / Publicitario",
        desc: "Producir imagenes para campanas publicitarias, vallas, sitios web y materiales de marketing para marcas.",
        work: "Planificacion de pre-produccion con agencias, sesiones en estudio y locacion, imagenes de producto y estilo de vida, colaboracion en equipo.",
        skills: "Iluminacion y composicion de alto nivel, desarrollo de conceptos creativos, comunicacion con clientes y agencias, experiencia en post-produccion.",
        salary: "$40,000 – $100,000+ (uno de los campos mejor pagados en fotografia)",
      },
      {
        id: "food",
        icon: "ForkKnife",
        title: "Fotografo de Alimentos",
        desc: "Hacer que la comida luzca irresistible para restaurantes, libros de cocina, empaques y contenido de redes sociales.",
        work: "Sesiones de menu de restaurantes, imagenes para libros de cocina, contenido de blogs de recetas, fotos de diseno de empaques, campanas de marcas de alimentos.",
        skills: "Colaboracion de estilismo, control de luz natural y artificial, precision de color, habilidades de macro/acercamiento.",
        salary: "$30,000 – $70,000 (plantilla/freelance); los mejores con clientes de marca ganan $80K+",
      },
      {
        id: "architecture",
        icon: "Buildings",
        title: "Fotografo de Arquitectura / Bienes Raices",
        desc: "Fotografiar edificios, interiores y propiedades para arquitectos, desarrolladores y agentes inmobiliarios.",
        work: "Sesiones de interiores y exteriores, tours virtuales, fotografia aerea/dron, sesiones crepusculares, HDR y correccion de perspectiva.",
        skills: "Experiencia con lentes tilt-shift y gran angular, bracketing HDR, correccion de perspectiva, pilotaje de drones, mezcla de luz ambiente y flash.",
        salary: "$35,000 – $80,000 (fotografos de alto volumen inmobiliario pueden ganar $100K+)",
      },
      {
        id: "wildlife",
        icon: "Butterfly",
        title: "Fotografo de Fauna / Naturaleza",
        desc: "Documentar animales y entornos naturales para conservacion, editorial y arte.",
        work: "Trabajo de campo en ubicaciones remotas, largas horas esperando comportamiento animal, envios editoriales, alianzas de conservacion, venta de impresiones.",
        skills: "Paciencia extrema, conocimiento de comportamiento animal, tecnica de teleobjetivo largo, habilidades al aire libre, post-procesado natural.",
        salary: "$25,000 – $60,000 (muchos complementan con talleres, impresiones y licencias de stock)",
      },
      {
        id: "fineart",
        icon: "PaintBrush",
        title: "Fotografo de Arte",
        desc: "Crear fotografias como expresion artistica para galerias, museos, coleccionistas y exhibiciones.",
        work: "Proyectos personales, exhibiciones en galerias, ferias de arte, venta de ediciones limitadas, residencias artisticas, ensenanza.",
        skills: "Vision artistica fuerte, pensamiento conceptual, experiencia en impresion y presentacion, networking en galerias, redaccion de becas.",
        salary: "Altamente variable ($15,000 – $100,000+); ingresos por ventas, exhibiciones, becas y ensenanza.",
      },
      {
        id: "medical",
        icon: "FirstAid",
        title: "Fotografo Medico / Cientifico",
        desc: "Crear imagenes precisas para registros medicos, publicaciones de investigacion, libros de texto y documentacion quirurgica.",
        work: "Documentacion de sala de operaciones, imagenes de patologia, fotografia de laboratorio, catalogos de dispositivos medicos.",
        skills: "Precision tecnica, protocolos de ambiente esteril, fotografia macro, precision de color, comprension de terminologia medica y anatomia.",
        salary: "$40,000 – $70,000 (plantilla hospitalaria); roles especializados con certificaciones pueden ganar mas.",
      },
      {
        id: "forensic",
        icon: "Detective",
        title: "Fotografo Forense",
        desc: "Documentar escenas del crimen, evidencia y accidentes con imagenes precisas y sin alterar para procedimientos legales.",
        work: "Documentacion de escenas del crimen, fotografia de evidencia, reconstruccion de accidentes, presentacion en tribunales.",
        skills: "Atencion meticulosa al detalle, cadena de custodia, fotografia macro y de escala, balance de flash y ambiente, redaccion de informes.",
        salary: "$35,000 – $65,000 (fuerzas del orden); consultores forenses privados pueden ganar mas.",
      },
      {
        id: "aerial",
        icon: "Airplane",
        title: "Fotografo Aereo / Dron",
        desc: "Capturar perspectivas aereas impresionantes para bienes raices, cine, mapeo y proyectos creativos.",
        work: "Tours aereos inmobiliarios, documentacion de progreso de construccion, produccion de cine/video, mapeo y levantamiento, proyectos creativos.",
        skills: "Pilotaje de drones (certificacion requerida), comprension de regulaciones de espacio aereo, composicion desde arriba, habilidades de video.",
        salary: "$35,000 – $80,000 (pilotos de dron inmobiliario); trabajo industrial o cinematografico puede pagar significativamente mas.",
      },
      {
        id: "stock",
        icon: "Images",
        title: "Fotografo de Stock",
        desc: "Construir una biblioteca de imagenes licenciadas a traves de agencias de stock para uso en anuncios, sitios web y publicaciones.",
        work: "Sesiones autodirigidas basadas en tendencias de mercado, optimizacion de palabras clave, gestion de portafolios en multiples plataformas.",
        skills: "Investigacion de mercado, estilo de disparo versatil, cesiones de modelo y conocimiento legal, flujo de edicion de alto volumen, SEO.",
        salary: "Altamente variable ($5,000 – $100,000+); contribuyentes top con portafolios grandes ganan ingresos pasivos.",
      },
      {
        id: "editor",
        icon: "PencilSimple",
        title: "Editor de Fotos / Retocador",
        desc: "Especializarse en post-produccion, transformando imagenes crudas en productos finales pulidos.",
        work: "Correccion de color, retoque de piel, creacion de composites, edicion por lotes, colaboracion de diseno editorial, procesamiento de imagenes de e-commerce.",
        skills: "Photoshop y Lightroom avanzado, ciencia del color, atencion al detalle, entrega rapida, comprension de preferencias de estilo del cliente.",
        salary: "$30,000 – $65,000 (plantilla); retocadores freelance especializados en belleza/moda pueden ganar $80K+.",
      },
      {
        id: "travel",
        icon: "TreePalm",
        title: "Fotografo de Viajes",
        desc: "Documentar destinos, culturas y experiencias para oficinas de turismo, revistas y marcas.",
        work: "Sesiones de destino, documentacion cultural, fotografia de hoteles y resorts, contenido de blog de viajes, reportajes de revistas.",
        skills: "Adaptabilidad a entornos diversos, narrativa, eficiencia de equipaje ligero, sensibilidad cultural, habilidades de escritura, presencia en redes sociales.",
        salary: "$25,000 – $70,000 (plantilla/freelance); alianzas de marca y redes sociales pueden aumentar ingresos significativamente.",
      },
      {
        id: "underwater",
        icon: "Waves",
        title: "Fotografo Subacuatico",
        desc: "Capturar vida marina, paisajes subacuaticos y sujetos humanos bajo la superficie del agua.",
        work: "Documentacion de biologia marina, imagenes de turismo de buceo, trabajo editorial/conservacion, sesiones de moda subacuaticas.",
        skills: "Certificacion de buceo, experiencia con carcasas subacuaticas, colocacion de strobes bajo el agua, correccion de color, conocimiento de biologia marina.",
        salary: "$30,000 – $70,000; trabajo especializado de resort y conservacion puede pagar mas. Los costos de equipo son altos.",
      },
      {
        id: "concert",
        icon: "MusicNote",
        title: "Fotografo de Conciertos / Musica",
        desc: "Fotografiar presentaciones en vivo, arte de album e imagenes promocionales para musicos y venues.",
        work: "Cobertura de shows en vivo (a menudo primeras 3 canciones, sin flash), arte de album, sesiones promocionales, cobertura de festivales.",
        skills: "Dominio de poca luz, lentes prime rapidos, timing y anticipacion, etiqueta del pit fotografico, networking con publicistas y artistas.",
        salary: "$20,000 – $55,000 (freelance/editorial); los mejores con contratos de gira ganan mas.",
      },
      {
        id: "newborn",
        icon: "Baby",
        title: "Fotografo de Recien Nacidos / Familia",
        desc: "Especializarse en el delicado arte de fotografiar recien nacidos, bebes y familias en momentos importantes.",
        work: "Sesiones de recien nacido (primeras 2 semanas), sesiones de maternidad, hitos (3, 6, 12 meses), retratos familiares, sesiones de cake smash.",
        skills: "Seguridad y poses de recien nacidos (critico), paciencia, tecnicas calmantes, iluminacion suave/calida, estilo de edicion suave, comunicacion con padres.",
        salary: "$25,000 – $60,000 (empleado de estudio); especialistas independientes en areas metropolitanas pueden ganar $80K+.",
      },
      {
        id: "space",
        icon: "Rocket",
        title: "Astrofotografo",
        desc: "Capturar el cielo nocturno, eventos celestiales y objetos del espacio profundo usando tecnicas especializadas de larga exposicion.",
        work: "Fotografia de paisaje nocturno, composiciones de Via Lactea, star trails, imagenes lunares/planetarias, fotografia de telescopio, timelapses.",
        skills: "Dominio de larga exposicion, comprension de mecanica celestial, alineacion polar, software de apilado, logistica de sitios oscuros, post-procesado.",
        salary: "$20,000 – $50,000 (mayormente freelance/venta de impresiones); ingresos complementados con talleres, tutoriales y licencias de stock.",
      },
    ],
  },
};

const iconMap = {
  Heart,
  Newspaper,
  Lightning,
  User,
  Storefront,
  ForkKnife,
  Buildings,
  Butterfly,
  PaintBrush,
  FirstAid,
  Detective,
  Airplane,
  Images,
  PencilSimple,
  TreePalm,
  Waves,
  MusicNote,
  Aperture,
  Baby,
  Rocket,
};

export default function CareersPage() {
  const { lang } = useTranslation();
  const c = careers[lang] || careers.en;
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="careers-page">
      <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
        <motion.div variants={item}>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
            {c.page_title}
          </h1>
          <p className="text-[#71717A] text-base">{c.page_sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-3">
          {c.items.map((career) => {
            const Icon = iconMap[career.icon] || Briefcase;
            const isOpen = expanded === career.id;

            return (
              <motion.div
                key={career.id}
                variants={item}
                className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden hover:border-white/15 transition-colors"
                data-testid={`career-${career.id}`}
              >
                <button
                  onClick={() => toggle(career.id)}
                  className="w-full flex items-center gap-4 p-4 sm:p-5 text-left"
                  data-testid={`career-toggle-${career.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#F5A524]/10 flex items-center justify-center shrink-0">
                    <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Outfit'] text-white font-medium">{career.title}</h3>
                    <p className="text-[#71717A] text-sm truncate">{career.desc}</p>
                  </div>
                  {isOpen ? (
                    <CaretUp className="w-5 h-5 text-[#71717A] shrink-0" />
                  ) : (
                    <CaretDown className="w-5 h-5 text-[#71717A] shrink-0" />
                  )}
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 space-y-3 border-t border-white/5 pt-4">
                        <p className="text-[#D4D4D8] text-sm leading-relaxed">{career.desc}</p>

                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">
                            {c.typical_work}
                          </h4>
                          <p className="text-[#A1A1AA] text-sm leading-relaxed">{career.work}</p>
                        </div>

                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">
                            {c.skills_needed}
                          </h4>
                          <p className="text-[#A1A1AA] text-sm leading-relaxed">{career.skills}</p>
                        </div>

                        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">
                            {c.salary_range}
                          </h4>
                          <p className="text-white text-sm font-['JetBrains_Mono']">{career.salary}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
